import { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { Loading } from '../components/Loading';
import { Toast, ToastProps } from '../components/Toast';

interface NotificationContextValues {
  showToast: (c: ToastProps, duration?: number) => void;
  toggleLoading: (value: boolean) => void;
  showErrorToast: (message: string, title?: string, duration?: number) => void;
  showInfoToast: (message: string, title?: string, duration?: number) => void;
  showSuccessToast: (message: string, title?: string, duration?: number) => void;
}

export const NotificationContext = createContext<NotificationContextValues>({
  showToast: () => {
    throw new Error('showToast function not implemented');
  },
  toggleLoading: () => {
    throw new Error('toggleLoading function not implemented');
  },
  showErrorToast: () => {
    throw new Error('showErrorToast function not implemented');
  },
  showInfoToast: () => {
    throw new Error('showErrorToast function not implemented');
  },
  showSuccessToast: () => {
    throw new Error('showSuccessToast function not implemented');
  },
});

type NotificationContextProviderProps = PropsWithChildren<{}>;

export const NotificationContextProvider: React.FC<NotificationContextProviderProps> = ({
  children,
}) => {
  const [loading, setLoading] = useState(false);
  const [toastStack, setToastStack] = useState<ToastProps[]>([]);

  const showToast = useCallback((c: ToastProps, duration = 3000) => {
    const closeToast = () => {
      setToastStack((prev) => prev.filter((t) => t !== toast));
      toast.onClose?.();
    };
    const toast = {
      ...c,
      onClose: closeToast,
    };
    setToastStack((prev) => [...prev, toast]);
    setTimeout(() => {
      setToastStack((prev) => prev.filter((t) => t !== toast));
    }, duration);
  }, []);

  const showErrorToast = useCallback(
    (message: string, title: string = '', duration = 3000) => {
      showToast(
        {
          title,
          message,
          type: 'error',
        },
        duration
      );
    },
    [showToast]
  );

  const showInfoToast = useCallback(
    (message: string, title: string = '', duration = 3000) => {
      showToast(
        {
          title,
          message,
          type: 'info',
        },
        duration
      );
    },
    [showToast]
  );

  const showSuccessToast = useCallback(
    (message: string, title: string = '', duration = 3000) => {
      showToast(
        {
          title,
          message,
          type: 'success',
        },
        duration
      );
    },
    [showToast]
  );

  return (
    <NotificationContext.Provider
      value={{
        showToast,
        toggleLoading: setLoading,
        showErrorToast,
        showInfoToast,
        showSuccessToast,
      }}
    >
      {children}
      <div className="fixed top-4 right-4 z-50 space-y-3 w-80">
        {toastStack.map((toast, index) => (
          <Toast key={index} {...toast} />
        ))}
      </div>
      <Loading open={loading} />
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = (): NotificationContextValues =>
  useContext(NotificationContext);
